/**
 * Rahmen der angemeldeten Ansicht: Kopfleiste, Betriebswechsler,
 * Impersonations-Banner, Fußzeile.
 */

import { AnimatePresence, motion } from 'framer-motion'
import { NavLink, Outlet } from 'react-router-dom'
import { useState } from 'react'

import { api, ROLE_LABELS, type Me } from '../lib/api'
import { springSnappy, springSoft } from '../lib/motion'
import { resolvedTheme, useLogout, useSwitchTenant, useThemeStore } from '../lib/session'
import { MoonIcon, SunIcon } from './icons'
import { Button } from '../ui'
import './shell.css'

export function Shell({ me }: { me: Me }) {
  const logout = useLogout()
  const [menuOpen, setMenuOpen] = useState(false)

  const links = [
    { to: '/', label: 'Übersicht', end: true },
    { to: '/meine-schichten', label: 'Meine Schichten' },
    { to: '/tausch', label: 'Tausch' },
    // Das Planungsraster nur für die, die planen — für alle anderen wäre es
    // ein Menüpunkt, hinter dem sie nichts tun können.
    ...(me.active_tenant?.role === 'owner' || me.active_tenant?.role === 'manager'
      ? [{ to: '/plan', label: 'Plan' }]
      : []),
    { to: '/team', label: 'Team' },
    ...(me.active_tenant?.role === 'owner' ? [{ to: '/betrieb', label: 'Betrieb' }] : []),
    { to: '/profil', label: 'Profil' },
    ...(me.is_superadmin ? [{ to: '/verwaltung', label: 'Verwaltung' }] : []),
  ]

  return (
    <>
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>

      {me.impersonated_by && <ImpersonationBanner me={me} />}

      <header className="shell__bar">
        <div className="shell__inner">
          <div className="shell__brand">
            <BrandMark />
            <TenantSwitcher me={me} />
          </div>

          <button
            className="shell__burger"
            aria-expanded={menuOpen}
            aria-controls="hauptnavigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? 'Schließen' : 'Menü'}
          </button>

          <nav
            id="hauptnavigation"
            className={`shell__nav ${menuOpen ? 'shell__nav--open' : ''}`}
            aria-label="Hauptnavigation"
          >
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) => `shell__link ${isActive ? 'is-active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {({ isActive }) => (
                  <>
                    <span className="shell__label">{link.label}</span>
                    {isActive && (
                      // Shared Element: Die Pille wandert zwischen den
                      // Einträgen, statt an jedem neu aufzublitzen.
                      <motion.span
                        layoutId="nav-pille"
                        className="shell__pill"
                        transition={springSnappy()}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            <div className="shell__nav-actions">
              <ThemeToggle />
              <Button
                variant="text"
                small
                onClick={() => logout.mutate()}
                busy={logout.isPending}
              >
                Abmelden
              </Button>
            </div>
          </nav>
        </div>
      </header>

      <main id="inhalt" className="shell__main">
        <Outlet />
      </main>

      <Footer />
    </>
  )
}

function BrandMark() {
  return (
    <span className="shell__logo" aria-hidden="true">
      <svg viewBox="0 0 32 32" width="28" height="28">
        <rect width="32" height="32" rx="8" fill="var(--md-sys-color-primary)" />
        <rect
          x="7"
          y="9"
          width="18"
          height="15"
          rx="2.5"
          fill="none"
          stroke="var(--md-sys-color-on-primary)"
          strokeWidth="2"
        />
        <path d="M7 14h18" stroke="var(--md-sys-color-on-primary)" strokeWidth="2" />
        <path
          d="M11 7v4M21 7v4"
          stroke="var(--md-sys-color-on-primary)"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <rect x="10.5" y="17" width="4" height="4" rx="1" fill="var(--md-sys-color-on-primary)" />
      </svg>
    </span>
  )
}

function TenantSwitcher({ me }: { me: Me }) {
  const switchTenant = useSwitchTenant()

  if (me.tenants.length === 0) {
    return <span className="muted">Kein Betrieb</span>
  }

  if (me.tenants.length === 1) {
    return (
      <span className="shell__tenant">
        <strong>{me.tenants[0]!.name}</strong>
        <span className="muted"> · {ROLE_LABELS[me.tenants[0]!.role]}</span>
      </span>
    )
  }

  return (
    <label className="shell__tenant">
      <span className="sr-only">Betrieb wechseln</span>
      <select
        className="shell__select"
        value={me.active_tenant?.id ?? ''}
        disabled={switchTenant.isPending}
        onChange={(event) => switchTenant.mutate(event.target.value)}
      >
        {me.tenants.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name} · {ROLE_LABELS[tenant.role]}
          </option>
        ))}
      </select>
    </label>
  )
}

function ThemeToggle() {
  const mode = useThemeStore((state) => state.mode)
  const setMode = useThemeStore((state) => state.setMode)

  // ⚠️ Gegen das **angezeigte** Thema umschalten, nicht gegen das
  // gespeicherte. Steht die Einstellung auf „System" und das System auf
  // dunkel, bot der Knopf vorher „Dunkel" an — ein Klick änderte dann sichtbar
  // gar nichts, weil es bereits dunkel war.
  const aktuell = resolvedTheme(mode)
  const next = aktuell === 'dark' ? 'light' : 'dark'
  const label = next === 'dark' ? 'Zu dunklem Design wechseln' : 'Zu hellem Design wechseln'

  return (
    <Button
      variant="text"
      className="btn--icon"
      onClick={() => setMode(next)}
      aria-label={label}
      title={label}
    >
      {/* Der Knopf zeigt, wohin es geht — nicht, wo man ist. Ein Mond heißt
          „hier wird es dunkel", so wie es die meisten Anwendungen halten. */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={next}
          className="theme-toggle__icon"
          initial={{ rotate: -70, scale: 0.5, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          exit={{ rotate: 70, scale: 0.5, opacity: 0 }}
          transition={springSnappy()}
        >
          {next === 'dark' ? <MoonIcon /> : <SunIcon />}
        </motion.span>
      </AnimatePresence>
    </Button>
  )
}

function ImpersonationBanner({ me }: { me: Me }) {
  const ends = me.impersonation_ends_at ? new Date(me.impersonation_ends_at) : null

  return (
    <motion.div
      className="impersonation"
      role="alert"
      initial={{ y: -40 }}
      animate={{ y: 0 }}
      transition={springSoft()}
    >
      <strong>Sitzungsübernahme aktiv.</strong>{' '}
      <span>
        {me.impersonated_by} handelt gerade als {me.full_name}
        {ends &&
          ` · endet um ${ends.toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
          })}`}
        .
      </span>
      <EndImpersonation />
    </motion.div>
  )
}

function EndImpersonation() {
  const [busy, setBusy] = useState(false)
  return (
    <button
      className="impersonation__end"
      disabled={busy}
      onClick={async () => {
        setBusy(true)
        await api.delete('/api/admin/impersonate').catch(() => undefined)
        // Harter Neuaufbau: Nach dem Wechsel der Identität ist jeder
        // zwischengespeicherte Datensatz der falschen Person zugeordnet.
        window.location.assign('/verwaltung')
      }}
    >
      Beenden
    </button>
  )
}

export function Footer() {
  return (
    <footer className="shell__footer">
      © {new Date().getFullYear()} Martin Pfeffer |{' '}
      <a href="https://celox.io" rel="noreferrer">
        celox.io
      </a>
    </footer>
  )
}
